import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { setupStepStates, setupViewState } from "../agentCredentialSetup"
import { csrfToken } from "../lib/rails"
import { LoadingOrb } from "./LoadingOrb"

const SETUP_POLL_INTERVAL_MS = 2500
const SETUP_POLL_ERROR_INTERVAL_MS = 5000
const SETUP_POLL_ERROR_MESSAGE = "We cannot check the login status right now. Refresh the page or wait while we retry."
const SETUP_STEP_LABELS = [
  ["runner", "Start secure login runner"],
  ["open", "Open the login link"],
  ["authorize", "Authorize with your code"],
  ["verify", "Verified"]
]
const IDLE_STEP_STATES = { open: "upcoming", authorize: "upcoming", verify: "upcoming" }

function setupRunPath(path, runId, provider) {
  if (!runId && !provider) return path

  const origin = typeof window === "undefined" ? "http://localhost" : window.location.origin
  const url = new URL(path, origin)
  if (runId) url.searchParams.set("setup_run_id", runId)
  if (provider) url.searchParams.set("provider", provider)

  return url.toString()
}

export function credentialSetupStatusRequest(credentialSetup, runId) {
  const statusPath = credentialSetup?.statusPath
  if (!statusPath) return null

  return {
    path: setupRunPath(statusPath, runId, credentialSetup.provider),
    options: {
      headers: { Accept: "application/json" }
    }
  }
}

export function credentialSetupCreateRequest(credentialSetup, authenticityToken = csrfToken()) {
  const createPath = credentialSetup?.createPath
  if (!createPath) return null

  return {
    path: createPath,
    options: {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": authenticityToken
      },
      body: JSON.stringify({ provider: credentialSetup.provider || "codex" })
    }
  }
}

export function credentialSetupAbandonRequest(credentialSetup, runId, authenticityToken = csrfToken()) {
  const abandonLoginPath = credentialSetup?.abandonLoginPath
  if (!abandonLoginPath || !runId) return null

  return {
    path: setupRunPath(abandonLoginPath, runId, credentialSetup.provider),
    options: {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": authenticityToken
      }
    }
  }
}

export function credentialSetupView(setup) {
  if (!setup || !setup.run_id) return "idle"
  if (!setup.terminal) return "active"

  return setup.status === "succeeded" ? "success" : "failed"
}

export function formatSetupElapsedTime(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0))
  const minutes = Math.floor(total / 60)
  const remainder = total % 60

  return `${minutes}:${String(remainder).padStart(2, "0")}`
}

// The runner step is "active" while the runner boots and the login link is not
// ready yet, so no user-facing step pretends to be actionable before its time.
export function credentialSetupRunnerStepState(view, setup) {
  if (view === "success") return "done"
  if (view === "failed") return "upcoming"
  if (!setup || !setup.authorization_uri) return "active"

  return "done"
}

function CredentialSetupWaiting({ elapsedSeconds, label }) {
  return (
    <div className="credential-setup-waiting" role="status">
      <LoadingOrb className="credential-setup-waiting-orb" size={64} state="working" />
      <p className="credential-setup-waiting-label">{label}</p>
      <span className="credential-setup-progress" aria-hidden="true"><span /></span>
      <p className="credential-setup-elapsed">
        {formatSetupElapsedTime(elapsedSeconds)} elapsed · usually ready within 5 minutes
      </p>
    </div>
  )
}

export function CredentialSetupDialog({
  actionPending = false,
  codeInputRef = null,
  copied = false,
  credentialSetup,
  elapsedSeconds = 0,
  loading = false,
  onAbandon,
  onClose,
  onConnect,
  onCopyCode,
  onGenerate,
  pollError = null,
  setup = null
}) {
  const view = credentialSetupView(setup)
  const viewState = view === "idle" ? null : setupViewState(setup, "Codex")
  const reconnect = credentialSetup?.mode === "reconnect"
  const title = viewState?.title || (reconnect ? "Reconnect Codex" : "Connect Codex")
  const copy = view === "idle"
    ? reconnect
      ? "Your saved Codex login is no longer valid. Starting a fresh secure login now."
      : "Pull requests are generated by Codex running under your own account. Starting your secure login now."
    : viewState.copy
  const steps = {
    runner: credentialSetupRunnerStepState(view, setup),
    ...(setup ? setupStepStates(setup) : IDLE_STEP_STATES)
  }
  const setupError = setup?.failure_message || setup?.authorization_code_error
  const idleStartFailed = view === "idle" && Boolean(pollError) && !loading
  const waitingForLink = (view === "active" && !setup.authorization_uri) || (view === "idle" && !idleStartFailed)

  function handleBackdropClick(event) {
    if (event.target === event.currentTarget) onClose?.()
  }

  return (
    <div className="stack-modal-backdrop" onClick={handleBackdropClick} role="presentation">
      <div className="focus-card stack-selection-modal credential-setup-modal" role="dialog" aria-modal="true" aria-labelledby="credential-setup-modal-title">
        <header className="credential-setup-modal-header">
          <img className="agent-provider-logo" src="/codegen-logos/codex.png" alt="" />
          <h2 id="credential-setup-modal-title">{title}</h2>
          {viewState ? <span className="agent-provider-status">{viewState.statusLabel}</span> : null}
          <button type="button" className="credential-setup-modal-close" aria-label="Close" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="credential-setup-modal-body">
          <p className="credential-setup-modal-copy">{copy}</p>

          <ol className="agent-steps">
            {SETUP_STEP_LABELS.map(([step, label]) => (
              <li key={step} className={`agent-step is-${steps[step] || "upcoming"}`}>
                <span className="agent-step-marker" aria-hidden="true"></span>
                <span className="agent-step-label">{label}</span>
              </li>
            ))}
          </ol>

          {pollError ? <p className="queue-flash queue-flash-alert" role="alert">{pollError}</p> : null}

          {waitingForLink ? (
            <CredentialSetupWaiting
              elapsedSeconds={elapsedSeconds}
              label={viewState?.title || "Starting secure login"}
            />
          ) : null}

          {view === "active" && setup.authorization_uri ? (
            <div className="credential-setup-action-card">
              <a className="repository-update-button agent-primary-button" href={setup.authorization_uri} target="_blank" rel="noopener">Open Codex login</a>
              {setup.user_code ? (
                <div className="agent-code-row">
                  <label className="agent-code-field">
                    <span>User code</span>
                    <input
                      ref={codeInputRef}
                      type="text"
                      readOnly
                      value={setup.user_code}
                      aria-label="Codex user code"
                      onFocus={(event) => event.target.select()}
                    />
                  </label>
                  <button type="button" className="agent-code-copy" onClick={onCopyCode}>{copied ? "Copied" : "Copy"}</button>
                </div>
              ) : null}
              <p className="agent-provider-note">Enter this code on the Codex login page — we detect the login automatically, no need to come back and confirm.</p>
            </div>
          ) : null}

          {view === "active" ? (
            <>
              {setupError ? <p className="queue-flash queue-flash-alert" role="alert">{setupError}</p> : null}
              <div className="credential-setup-modal-actions">
                <button type="button" className="agent-quiet-action" disabled={actionPending} onClick={onAbandon}>Abandon login</button>
              </div>
            </>
          ) : null}

          {view === "failed" || idleStartFailed ? (
            <div className="credential-setup-modal-actions">
              <button type="button" className="repository-update-button agent-primary-button" disabled={actionPending} onClick={onConnect}>Try again</button>
            </div>
          ) : null}

          {view === "success" ? (
            <>
              <p className="agent-provider-note">Codex is connected and selected for tasks.</p>
              <div className="credential-setup-modal-actions">
                <button type="button" className="repository-update-button agent-primary-button" onClick={onGenerate}>Generate PR</button>
                <button type="button" className="agent-quiet-action" onClick={onClose}>Done</button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function CredentialSetupModal({ credentialSetup, onClose, onCredentialsReady, onGenerate }) {
  const [setup, setSetup] = useState(null)
  const [loading, setLoading] = useState(true)
  const [actionPending, setActionPending] = useState(false)
  const [pollError, setPollError] = useState(null)
  const [copied, setCopied] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const cancelledRef = useRef(false)
  const pollTimerRef = useRef(null)
  const copiedTimerRef = useRef(null)
  const setupRef = useRef(null)
  const pollFailuresRef = useRef(0)
  const readyNotifiedRef = useRef(false)
  const autoStartRef = useRef(false)
  const openedAtRef = useRef(null)
  const codeInputRef = useRef(null)

  function clearPollTimer() {
    if (!pollTimerRef.current) return

    window.clearTimeout(pollTimerRef.current)
    pollTimerRef.current = null
  }

  function applySetupPayload(payload) {
    const nextSetup = payload?.setup?.run_id ? payload.setup : null

    setupRef.current = nextSetup
    setSetup(nextSetup)
    clearPollTimer()
    if (nextSetup?.open) {
      pollTimerRef.current = window.setTimeout(pollSetupStatus, SETUP_POLL_INTERVAL_MS)
    }
  }

  // Opening the modal is the user's confirmation — when no login run is open
  // yet, start one right away instead of asking for a second click.
  function autoStartSetup(payload) {
    if (autoStartRef.current || payload?.setup?.run_id) return

    autoStartRef.current = true
    connect()
  }

  function pollSetupStatus() {
    const request = credentialSetupStatusRequest(credentialSetup, setupRef.current?.run_id)
    if (!request) return

    fetch(request.path, request.options)
      .then((response) => {
        if (!response.ok) throw new Error(`Credential setup status failed: ${response.status}`)
        return response.json()
      })
      .then((payload) => {
        if (cancelledRef.current) return

        pollFailuresRef.current = 0
        setLoading(false)
        setPollError(null)
        applySetupPayload(payload)
        autoStartSetup(payload)
      })
      .catch(() => {
        if (cancelledRef.current) return

        pollFailuresRef.current += 1
        if (pollFailuresRef.current > 1) setPollError(SETUP_POLL_ERROR_MESSAGE)
        clearPollTimer()
        pollTimerRef.current = window.setTimeout(pollSetupStatus, SETUP_POLL_ERROR_INTERVAL_MS)
      })
  }

  useEffect(() => {
    cancelledRef.current = false
    openedAtRef.current = Date.now()
    const elapsedTimer = window.setInterval(() => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - openedAtRef.current) / 1000)))
    }, 1000)
    const request = credentialSetupStatusRequest(credentialSetup)
    if (!request) {
      setLoading(false)
      return () => {
        cancelledRef.current = true
        window.clearInterval(elapsedTimer)
      }
    }

    fetch(request.path, request.options)
      .then((response) => {
        if (!response.ok) throw new Error(`Credential setup status failed: ${response.status}`)
        return response.json()
      })
      .then((payload) => {
        if (cancelledRef.current) return

        setLoading(false)
        applySetupPayload(payload)
        autoStartSetup(payload)
      })
      .catch(() => {
        if (cancelledRef.current) return

        setPollError(SETUP_POLL_ERROR_MESSAGE)
        clearPollTimer()
        pollTimerRef.current = window.setTimeout(pollSetupStatus, SETUP_POLL_ERROR_INTERVAL_MS)
      })

    return () => {
      cancelledRef.current = true
      clearPollTimer()
      window.clearInterval(elapsedTimer)
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current)
    }
  }, [])

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose?.()
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  useEffect(() => {
    if (readyNotifiedRef.current) return
    if (!setup?.terminal || setup?.status !== "succeeded") return

    readyNotifiedRef.current = true
    onCredentialsReady?.()
  }, [setup, onCredentialsReady])

  function connect() {
    const request = credentialSetupCreateRequest(credentialSetup)
    if (actionPending) return
    if (!request) {
      setPollError("Could not start the Codex login. Try again.")
      return
    }

    setActionPending(true)
    setPollError(null)

    fetch(request.path, request.options)
      .then((response) => response.json().then((payload) => ({ payload, response })))
      .then(({ payload, response }) => {
        if (cancelledRef.current) return

        setActionPending(false)
        if (!response.ok) {
          setPollError(payload?.error || "Could not start the Codex login. Try again.")
          return
        }
        applySetupPayload(payload)
      })
      .catch(() => {
        if (cancelledRef.current) return

        setActionPending(false)
        setPollError("Could not start the Codex login. Try again.")
      })
  }

  function abandon() {
    const request = credentialSetupAbandonRequest(credentialSetup, setupRef.current?.run_id)
    if (!request || actionPending) return

    setActionPending(true)
    setPollError(null)

    fetch(request.path, request.options)
      .then((response) => response.json().then((payload) => ({ payload, response })))
      .then(({ payload, response }) => {
        if (cancelledRef.current) return

        setActionPending(false)
        if (!response.ok) {
          setPollError(payload?.error || "Could not abandon the Codex login. Try again.")
          return
        }
        applySetupPayload(payload)
      })
      .catch(() => {
        if (cancelledRef.current) return

        setActionPending(false)
        setPollError("Could not abandon the Codex login. Try again.")
      })
  }

  function copyUserCode() {
    const value = setupRef.current?.user_code
    if (!value) return

    const markCopied = () => {
      setCopied(true)
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current)
      copiedTimerRef.current = window.setTimeout(() => {
        copiedTimerRef.current = null
        setCopied(false)
      }, 1500)
    }
    const selectCode = () => {
      codeInputRef.current?.focus()
      codeInputRef.current?.select()
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(markCopied, selectCode)
    } else {
      selectCode()
    }
  }

  function handleGenerate() {
    onGenerate?.()
    onClose?.()
  }

  if (!credentialSetup) return null

  return createPortal(
    <CredentialSetupDialog
      credentialSetup={credentialSetup}
      setup={setup}
      loading={loading}
      actionPending={actionPending}
      pollError={pollError}
      copied={copied}
      elapsedSeconds={elapsedSeconds}
      codeInputRef={codeInputRef}
      onConnect={connect}
      onAbandon={abandon}
      onCopyCode={copyUserCode}
      onGenerate={handleGenerate}
      onClose={onClose}
    />,
    document.body
  )
}
